import React, {Component} from 'react';
import css from './Scoreboard.module.scss';
import SectionHeading from "../../components/SectionHeading/SectionHeading";
import TableBuilder from "../../components/TableBuilder/TableBuilder";
import getDateQry from "../../helpers/dateHelpers";
import {THIS_QUARTER} from "../../helpers/timePeriods";
import {PREVIOUS_QUARTER} from "../../helpers/timePeriods";
import {YEAR_TO_DATE} from "../../helpers/timePeriods";
import axios from "axios";
import moment from 'moment';
import _ from 'lodash';


/**
 * @class
 *
 * Scoreboard page.
 */
const Scoreboard = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    postRequestDataYearToDate = {
        fields: [
            'ContactMaster__c',
            'RepNameFormula__c',
            'Sales_Office__c',
            'Current_System_Size__c',
            'Deal_Value__c',
            'Pending_Sale_Date__c',
            'ContactMaster__r.Job_Title__c'
        ],
        where: {
            Fake__c: false,
            Void__c: false,
            "OppMaster__r.Fake__c": false,
            "ContactMaster__r.Job_Department__c": 'Sales',
            "ContactMaster__r.Active__c": true,
            "ContactMaster__r.Job_Title__c": { $ne: 'Inside Sales Rep' }
        },
        date: getDateQry(YEAR_TO_DATE, 'Completed_Sale_Date__c')
    };

    contactPostRequest(repIds) {
        return {
            fields: [
                'Id',
                'Job_Title__c',
                'Account.Sales_Office_Manager_Name__c',
                'Account.Sales_Office_Manager__c'
            ],
            where: {
                "Id": { "$in": repIds }
            }
        }
    };

    // ---------- state: ----------
    state = {
        tableData: [],
        topRookies: [],
        topManagers: [],
        repsLastQuarter: [],
        rooksLastQuarter: [],
        mgrsLastQuarter: [],
        repsYearToDate: []
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        const DAY_FORMAT = 'YYYY-MM-DDT00:00:00Z';

        axios.get('/data/find/kpi', { cancelToken: this.source.token })
            .then(async (res) => {
                const cache       = res.data.cache || {};
                const repKpis     = cache.rep || {};
                const officeKpis  = cache.office || {};

                const thisQuarterRepData   = repKpis.THIS_QUARTER || []; // "Top Reps This Quarter" and "Top Rookies This Quarter"
                const thisQuarterOfficeData = officeKpis.THIS_QUARTER || []; // "Top Managers This Quarter"
                const lastQuarterRepData = repKpis.PREVIOUS_QUARTER || []; // "Top Reps Last Quarter" and "Top Rookies Last Quarter"
                const lastQuarterOfficeData = officeKpis.PREVIOUS_QUARTER || []; // "Top Managers Last Quarter"

                // 1) One combined rep‑ID list (for both Quarters)
                const allRepIds = Array.from(new Set(
                    [...thisQuarterRepData, ...lastQuarterRepData]
                    .map(r => r.repId)
                    .filter(id => id != null)
                ));

                // 2) Fetch contact data for job title and manager names
                let contacts = [];
                try {
                    const contactResp = await axios.post('/sf/contact/findNo', this.contactPostRequest(allRepIds), { cancelToken: this.source.token});
                    contacts = contactResp.data.records;
                } catch (err) {
                    console.error('Error fetching contacts:', err);
                }
                const contactsMap = _.keyBy(contacts, 'Id');

                // 3) Reps + Rooks processing
                function processReps(records) {
                    const rookJobTitles = new Set(['Junior Rep', 'Rep-In-Training', 'Rookie']);

                    // TODO: Update KPIs data to have job title on the rep data so we can remove contact soql here -gc
                    const enriched = records
                        .map(r => ({ ...r, contact: contactsMap[r.repId] || {} }))
                        .filter(r => r.csd_count > 0);

                    const reps  = enriched.filter(r => !rookJobTitles.has(r.contact.Job_Title__c)) // getting only reps
                        .sort((a,b)=> b.csd_count - a.csd_count) // sorting by sold deals
                        .slice(0,25) // getting only the top 25
                        .map(r => ({ // getting correct styled objects
                            id:     r.repId,
                            name:   r.name,
                            office: r.office,
                            csd:    r.csd_count
                        }));

                    const rooks = enriched.filter(r =>  rookJobTitles.has(r.contact.Job_Title__c)) // getting only rooks
                        .sort((a,b)=> b.csd_count - a.csd_count) // sorting by sold deals
                        .slice(0,10) // getting only the top 10
                        .map(r => ({ // getting correct styled objects
                            id:     r.repId,
                            name:   r.name,
                            office: r.office,
                            csd:    r.csd_count
                        }));

                    return { reps, rooks };
                }

                // Render THIS Quarter reps
                const thisQuarterReps = processReps(thisQuarterRepData);
                this.setState({
                    tableData: thisQuarterReps.reps,
                    topRookies: thisQuarterReps.rooks
                });

                // Then render LAST Quarter reps
                const lastQuarterReps = processReps(lastQuarterRepData);
                this.setState({
                    repsLastQuarter:  lastQuarterReps.reps,
                    rooksLastQuarter: lastQuarterReps.rooks
                });

                // 4) Managers processing from office KPI slice
                function processManagers(officeRecords, enrichedReps) {
                    // TODO: Update KPIs data to store manager name on the office data to remove contact soql -gc
                    // Build a quick map: officeName → first seen manager info
                    const mgrInfoByOffice = {};
                    for (const r of enrichedReps) {
                        const off = r.office;
                        const acct = r.contact.Account || {};
                        if (off && acct.Sales_Office_Manager__c && !mgrInfoByOffice[off]) {
                        mgrInfoByOffice[off] = {
                            id:   acct.Sales_Office_Manager__c,
                            name: acct.Sales_Office_Manager_Name__c
                        };
                        }
                    }

                    // Now map each office KPI to a manager row
                    return officeRecords.map(o => {
                        const info = mgrInfoByOffice[o.office] || {};
                        return {
                            id:     info.id,
                            name:   info.name,
                            office: o.office,
                            csd:    Math.round(o.csd)
                        };
                    })
                    .filter(m => m.id && m.csd > 0)  // drop any offices without a manager match or sold deals
                    .sort((a,b) => b.csd - a.csd);
                }

                // Render THIS Quarter managers
                const thisQuarterMgrs = processManagers(thisQuarterOfficeData,
                    thisQuarterRepData.map(r=>({ ...r, contact: contactsMap[r.repId] || {} }))
                );
                this.setState({ topManagers: thisQuarterMgrs });

                // Render LAST Quarter managers
                const lastQuarterMgrs = processManagers(lastQuarterOfficeData,
                    lastQuarterRepData.map(r=>({ ...r, contact: contactsMap[r.repId] || {} }))
                );
                this.setState({ mgrsLastQuarter: lastQuarterMgrs });
            })
            .catch(err => {
                console.error('Error fetching KPI data:', err);
            });

        axios.post('/sf/repsaletracker/findNo',
            this.postRequestDataYearToDate,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const data = res.data.records;
                const filteredData = _.filter(data, function(o) {return o.Sales_Office__c !== 'Rise Energy / Safe Streets' && o.Sales_Office__c !== 'Elite Energy Consulting, Inc'});

                // Group records by their ContactMaster IDs:
                const groupedData = _.groupBy(filteredData, d => d.ContactMaster__c);

                // Sort the groupedData keys based on the length of their array values (in order to count + sort by total of RST records):
                const sortedKeys = _.orderBy(_.keys(groupedData), (g) => {
                    return groupedData[g].length;
                },'desc');

                // Cut down the length of the key array to 50:
                const sorted = _.take(sortedKeys, 25);

                // Assign sortedData the groupedData object limited to the 50 rep keys in sorted:
                const sortedData = _.pick(groupedData, sorted);

                // Create array of "rep objects":
                let repsYearToDate = _.map(sortedData, (g) => {
                    let size = 0;
                    let deals = 0;
                    let pends = 0;

                    // Loop through the array of records, adding up deal counts and system sizes:
                    _.each(g, function(v){
                        size += v.Current_System_Size__c;
                        deals++;
                        if (v.Pending_Sale_Date__c && moment(v.Pending_Sale_Date__c).format(DAY_FORMAT) >= moment().subtract(30, 'days').format(DAY_FORMAT)){
                            pends++;
                        }
                    });

                    // create object to return to add to the _.map array. The array will now look like:
                    // [{name,office,csd,size}, ...]
                    return {
                        id:     g[0].ContactMaster__c,
                        name:   g[0].RepNameFormula__c,
                        office: g[0].Sales_Office__c,
                        csd:    deals,
                        psd:    pends,
                        kw:     Math.round(size)
                    };
                });

                repsYearToDate.sort((a, b) => {
                    // sort data/array by highest sales (csd) first:
                    if (a.csd > b.csd) {
                        return -1;
                    }
                    // if sales are the same, sort by whoever has higher KW:
                    else if ((a.csd === b.csd) && (a.kw > b.kw)) {
                        return -1;
                    }
                    else {
                        return 1;
                    }
                });

                this.setState({ repsYearToDate });
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(POST) Error in Scoreboard: \n', err);
                }
            });
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    render() {
        return (
            <div className={css.Scoreboard}>
                <SectionHeading heading="Scoreboard" />
                <br></br>
                <h2>Top Reps This Quarter</h2>
                <TableBuilder reps={this.state.tableData}/>
                <br></br>
                <h2>Top Rookies This Quarter</h2>
                <TableBuilder reps={this.state.topRookies}/>
                <br></br>
                <h2>Top Managers This Quarter</h2>
                <TableBuilder reps={this.state.topManagers}/>
                <br></br>
                <h2>Top Reps Last Quarter</h2>
                <TableBuilder reps={this.state.repsLastQuarter}/>
                <br></br>
                <h2>Top Rookies Last Quarter</h2>
                <TableBuilder reps={this.state.rooksLastQuarter}/>
                <br></br>
                <h2>Top Managers Last Quarter</h2>
                <TableBuilder reps={this.state.mgrsLastQuarter}/>
                <br></br>
                <h2>Top Reps Year To Date</h2>
                <TableBuilder reps={this.state.repsYearToDate}/>
            </div>
        );
    };
};

export default Scoreboard;
